import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Trash2 } from "lucide-react";

import { MentionTextarea } from "@/components/MentionTextarea";
import { Button } from "@/components/ui/Button";
import { hubModalOverlayProps } from "@/components/ui/HubModalOverlay";
import { useHubToast } from "@/components/ui/HubToast";
import { HoverHintWithGlyph, ReadOnlyHoverLayer } from "@/components/ui/title-help";
import { ApiError, apiFetch } from "@/lib/api";
import { useIntegratorMe } from "@/lib/me-query";
import { cn } from "@/lib/utils";

const IMPORTED_PAPERWORK_NOTE_EMAIL = "imported-notes@hub.local";
const PAPERWORK_COMMENTS_HELP =
  "A series of comments on this shipment. Type @ to mention someone. Existing paperwork note text stays as the first entry. Completion override is separate and still replaces the shipment note when a row leaves the queue.";

export interface ShipmentPaperworkCommentRow {
  id: string;
  comment_text: string;
  created_at: string;
  created_by_email: string;
  created_by_display_name: string | null;
  deleted_at: string | null;
}

function authorLabel(comment: ShipmentPaperworkCommentRow): string {
  if (comment.created_by_email.trim().toLowerCase() === IMPORTED_PAPERWORK_NOTE_EMAIL) {
    return "Imported paperwork note";
  }
  const displayName = comment.created_by_display_name?.trim();
  if (displayName != null && displayName !== "") {
    return displayName;
  }
  return comment.created_by_email;
}

function emailsMatch(authorEmail: string, userEmail: string): boolean {
  return authorEmail.trim().toLowerCase() === userEmail.trim().toLowerCase();
}

function isDeleted(comment: ShipmentPaperworkCommentRow): boolean {
  return comment.deleted_at != null && comment.deleted_at.trim() !== "";
}

function formatWhen(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) {
    return iso;
  }
  return date.toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

export function PaperworkNoteCommentsModal({
  shipmentId,
  legacyNote,
  onClose,
  onChanged,
}: {
  shipmentId: number;
  legacyNote: string | null;
  onClose: () => void;
  onChanged: () => void;
}): JSX.Element {
  const queryClient = useQueryClient();
  const { showToast } = useHubToast();
  const meQuery = useIntegratorMe();
  const currentUserEmail = meQuery.data?.user.email ?? "";
  const [draft, setDraft] = useState("");
  const queryKey = ["shipping", "paperwork", "comments", shipmentId] as const;

  useEffect(() => {
    setDraft("");
  }, [shipmentId]);

  const commentsQuery = useQuery<{ comments: ShipmentPaperworkCommentRow[] }, ApiError>({
    queryKey,
    queryFn: () =>
      apiFetch<{ comments: ShipmentPaperworkCommentRow[] }>(
        `/api/shipping/paperwork/shipments/${String(shipmentId)}/comments`,
      ),
  });

  const comments = commentsQuery.data?.comments ?? [];
  const legacyText = legacyNote?.trim() ?? "";
  const showLegacy =
    commentsQuery.isSuccess &&
    comments.length === 0 &&
    legacyText !== "";
  const rows: ShipmentPaperworkCommentRow[] = showLegacy
    ? [
        {
          id: "legacy-note",
          comment_text: legacyText,
          created_at: "",
          created_by_email: IMPORTED_PAPERWORK_NOTE_EMAIL,
          created_by_display_name: "Imported paperwork note",
          deleted_at: null,
        },
        ...comments,
      ]
    : comments;

  const postMutation = useMutation<{ comment: ShipmentPaperworkCommentRow }, ApiError, string>({
    mutationFn: async (commentText) =>
      apiFetch<{ comment: ShipmentPaperworkCommentRow }>(
        `/api/shipping/paperwork/shipments/${String(shipmentId)}/comments`,
        { method: "POST", body: { comment_text: commentText } },
      ),
    onSuccess: () => {
      setDraft("");
      void queryClient.invalidateQueries({ queryKey });
      onChanged();
    },
    onError: (error) => {
      showToast({ message: error.message, tone: "error" });
    },
  });

  const deleteMutation = useMutation<void, ApiError, string>({
    mutationFn: async (commentId) => {
      await apiFetch(
        `/api/shipping/paperwork/shipments/${String(shipmentId)}/comments/${encodeURIComponent(commentId)}`,
        { method: "DELETE" },
      );
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey });
      onChanged();
    },
    onError: (error) => {
      showToast({ message: error.message, tone: "error" });
    },
  });

  const postBlocked = draft.trim() === "" || postMutation.isPending;
  const postHint = postMutation.isPending ? "Posting this comment." : "Write a comment before posting.";

  return (
    <div
      {...hubModalOverlayProps(false)}
      role="dialog"
      aria-modal="true"
      aria-labelledby="paperwork-note-comments-title"
      onClick={(event) => {
        if (event.target === event.currentTarget) {
          onClose();
        }
      }}
    >
      <div className="my-4 flex max-h-[90vh] w-full max-w-lg flex-col overflow-hidden rounded-lg border border-slate-200 bg-white shadow-xl">
        <div className="border-b border-slate-200 px-5 py-4">
          <div className="flex items-center gap-2">
            <h2 id="paperwork-note-comments-title" className="text-base font-semibold text-slate-900">
              Paperwork comments · Shipment {shipmentId}
            </h2>
            <HoverHintWithGlyph hoverHint={PAPERWORK_COMMENTS_HELP} size="sm" />
          </div>
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4">
          {commentsQuery.isLoading ? (
            <p className="text-sm text-slate-600">Loading comments…</p>
          ) : commentsQuery.isError ? (
            <p className="text-sm text-red-700">{commentsQuery.error.message}</p>
          ) : rows.length === 0 ? (
            <p className="text-sm text-slate-600">No comments yet.</p>
          ) : (
            <ul className="space-y-2">
              {rows.map((comment) => {
                const deleted = isDeleted(comment);
                const canDelete =
                  comment.id !== "legacy-note" &&
                  currentUserEmail !== "" &&
                  emailsMatch(comment.created_by_email, currentUserEmail) &&
                  !deleted;
                return (
                  <li
                    key={comment.id}
                    className={cn(
                      "rounded-md border border-slate-100 bg-slate-50/80 px-2 py-2 text-sm",
                      deleted && "text-slate-400",
                    )}
                  >
                    <p className={cn("whitespace-pre-wrap break-words", deleted && "line-through")}>
                      {comment.comment_text}
                    </p>
                    <div className="mt-1 flex items-center justify-between gap-2">
                      <p className="min-w-0 text-xs text-slate-500">
                        {authorLabel(comment)}
                        {comment.created_at !== "" ? (
                          <>
                            <span className="text-slate-400"> · </span>
                            {formatWhen(comment.created_at)}
                          </>
                        ) : null}
                      </p>
                      {canDelete ? (
                        <button
                          type="button"
                          className="inline-flex shrink-0 items-center gap-1 rounded px-1.5 py-0.5 text-xs font-medium text-slate-500 hover:bg-slate-100 hover:text-red-700"
                          aria-label="Delete your comment"
                          disabled={deleteMutation.isPending && deleteMutation.variables === comment.id}
                          onClick={() => deleteMutation.mutate(comment.id)}
                        >
                          <Trash2 className="h-3.5 w-3.5" aria-hidden />
                          Delete
                        </button>
                      ) : null}
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
          <label className="mt-4 block text-xs font-medium text-slate-600" htmlFor={`paperwork-comment-${shipmentId}`}>
            New comment
          </label>
          <MentionTextarea
            id={`paperwork-comment-${shipmentId}`}
            value={draft}
            onChange={setDraft}
            rows={3}
            className="mt-1 w-full resize-y rounded-md border border-slate-300 px-2 py-1.5 text-sm text-slate-900 shadow-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
            placeholder="Add a comment for the team. Type @ to mention someone."
          />
        </div>
        <div className="flex justify-end gap-2 border-t border-slate-100 px-5 py-3">
          <Button type="button" variant="secondary" onClick={onClose} disabled={postMutation.isPending}>
            Cancel
          </Button>
          <ReadOnlyHoverLayer active={postBlocked} hoverHint={postHint}>
            <Button
              type="button"
              variant="primary"
              disabled={postBlocked}
              onClick={() => postMutation.mutate(draft.trim())}
            >
              {postMutation.isPending ? "Posting…" : "Post comment"}
            </Button>
          </ReadOnlyHoverLayer>
        </div>
      </div>
    </div>
  );
}
